import { useRef, useEffect, useCallback, useMemo } from 'react';

import { debounce } from '../01-core/Utilities';
import { ButtonProps } from '../02-atoms/Button';
import { Media, MediaProps } from '../02-atoms/Media';
import { hasMedia } from '../02-atoms/Media.utils';
import { ButtonGroup } from '../03-molecules/ButtonGroup';

export interface LayeredContentItemProps {
  /** The title */
  title: string;
  /** The optional media */
  media?: MediaProps;
  /** The optional description */
  description?: string;
  /** The optional actions */
  actions?: ButtonProps[];
}

export interface LayeredContentProps {
  /** The groups */
  groups: LayeredContentItemProps[];
  /** Additional class */
  className?: string;
}

const isOriginalSizeMedia = (media?: MediaProps) => {
  return Boolean(media?.originalSize);
};

/** Block rendering a layered content block */
export const LayeredContent = ({ groups, className }: LayeredContentProps) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const onResize = useCallback(() => {
    const root = ref.current;
    if (!root) return;

    let offset = 0;
    const available = window.innerHeight;
    const heights: number[] = [];
    const titleHeights: number[] = [];
    let sticky = true;

    const groupsEl = root.querySelectorAll<HTMLElement>(
      '.layered-content__group',
    );

    groupsEl.forEach((groupEl, i) => {
      const titleEl = groupEl.querySelector<HTMLElement>(
        '.layered-content__title',
      );
      groupsEl[i].style.setProperty('--height', `0px`);
      const h = Math.max(
        Math.round(groupEl.getBoundingClientRect().height),
        i == 0 ? 0 : heights[i - 1] - titleHeights[i - 1],
      );
      titleHeights.push(
        Math.round(titleEl?.getBoundingClientRect().height ?? 0),
      );
      heights.push(h);
      groupsEl[i].style.setProperty('--height', `${h}px`);
    });

    let collapsedHeight = 0;
    titleHeights.forEach((h, i) => {
      collapsedHeight += i === titleHeights.length - 1 ? heights[i] : h;
    });
    let bottom = 0;

    groupsEl.forEach((groupEl, i) => {
      if (i < heights.length - 1 && heights[i] + offset > available) {
        sticky = false;
      }

      groupEl.style.setProperty('--offset', `${offset}px`);
      groupEl.style.setProperty('--offset-neg', `${i === 0 ? 0 : -bottom}px`);
      bottom =
        i === heights.length - 1 ? 0 : collapsedHeight - offset - heights[i];
      groupEl.style.setProperty('--offset-bottom', `${bottom}px`);
      offset += titleHeights[i];
    });

    root.classList.toggle('layered-content--sticky', sticky);
  }, []);

  const handleResize = useMemo(
    () => debounce(onResize, 100, false),
    [onResize],
  );

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.requestAnimationFrame(() => onResize());
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize, onResize]);

  return (
    <div ref={ref} className={['layered-content', className].join(' ')}>
      <div className="layered-content__wrapper">
        {groups.map((group, i) => (
          <div key={i} className="layered-content__group">
            <h3 className="layered-content__title">{group.title}</h3>
            <div
              className={[
                'layered-content__content',
                hasMedia(group.media)
                  ? 'layered-content__content--has-media'
                  : '',
              ].join(' ')}
            >
              {hasMedia(group.media) && (
                <Media
                  {...group.media}
                  className={[
                    'layered-content__media',
                    isOriginalSizeMedia(group.media)
                      ? 'layered-content__media--original-size'
                      : '',
                    group.media?.className ?? '',
                  ].join(' ')}
                />
              )}
              {group.description && <p>{group.description}</p>}
              {group.actions && <ButtonGroup items={group.actions} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
